import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// Define types
export interface User {
  id: string;
  email: string;
  name: string;
  displayName?: string;
  photoURL?: string;
  jobTitle?: string;
  organization?: string;
  bio?: string;
  createdAt?: string;
  lastLoginAt?: string;
  role?: string;
  created_at: string;
  subscription: {
    plan: string;
    status: string;
    expiry: string;
  };
  preferences: {
    ui_theme: string;
    notifications: boolean;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Define initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null
};

// Define async thunks
export const register = createAsyncThunk(
  'auth/register',
  async (userData: { email: string; password: string; name: string }, { rejectWithValue }) => {
    try {
      // Call the actual backend API
      const response = await apiService.post('/auth/register', userData);
      
      if (response.data.status !== 'success') {
        throw new Error('Registration failed');
      }
      
      // Store token in localStorage for future requests
      if (response.data.data.session?.access_token) {
        localStorage.setItem('authToken', response.data.data.session.access_token);
      }
      
      return {
        user: response.data.data.user,
        token: response.data.data.session?.access_token || null
      };
    } catch (error) {
      console.error('Registration error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Registration failed');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.log('[AUTH] Attempting login with credentials');
      
      // Call the actual backend API
      const response = await apiService.post('/auth/login', credentials);
      
      if (response.data.status !== 'success') {
        console.error('[AUTH] Login response indicated failure:', response.data);
        throw new Error(response.data.message || 'Login failed');
      }
      
      if (!response.data.data?.session?.access_token) {
        console.error('[AUTH] Login response missing access token:', response.data);
        throw new Error('Invalid response: missing authentication token');
      }
      
      // Store token in localStorage for future requests
      localStorage.setItem('authToken', response.data.data.session.access_token);
      
      console.log('[AUTH] Login successful');
      return {
        user: response.data.data.user,
        token: response.data.data.session.access_token
      };
    } catch (error: any) {
      console.error('[AUTH] Login error:', error);
      
      // Format user-friendly error message
      let errorMessage = 'Login failed';
      
      if (error.name === 'AuthError') {
        // Use the already formatted message from our API service
        errorMessage = error.message;
      } else if (error.response) {
        // Handle other response errors
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const loginWithProvider = createAsyncThunk(
  'auth/loginWithProvider',
  async (provider: string, { rejectWithValue }) => {
    try {
      // Call the actual backend API for social login
      const response = await apiService.post(`/auth/login/${provider}`);
      
      if (response.data.status !== 'success') {
        throw new Error(`Login with ${provider} failed`);
      }
      
      // For OAuth providers, we get a URL to redirect to
      if (response.data.data.url) {
        // Redirect the user to the OAuth provider
        window.location.href = response.data.data.url;
        return { redirecting: true };
      }
      
      // If we're not redirecting, it means we have user data and token
      // Store token in localStorage for future requests
      if (response.data.data.session?.access_token) {
        localStorage.setItem('authToken', response.data.data.session.access_token);
      }
      
      return {
        user: response.data.data.user,
        token: response.data.data.session?.access_token
      };
    } catch (error) {
      console.error(`Login with ${provider} error:`, error);
      return rejectWithValue(error instanceof Error ? error.message : `Login with ${provider} failed`);
    }
  }
);

export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async (userData: { displayName?: string; jobTitle?: string; organization?: string; bio?: string }, { rejectWithValue }) => {
    try {
      // Call the actual backend API to update user data
      const response = await apiService.put('/auth/me', userData);
      
      if (response.data.status !== 'success') {
        throw new Error('Failed to update user');
      }
      
      return response.data.data.user;
    } catch (error) {
      console.error('Update user error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update user');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Call the actual backend API to logout
      const response = await apiService.post('/auth/logout');
      
      if (response.data.status !== 'success') {
        throw new Error('Logout failed');
      }
      
      // Remove token from localStorage
      localStorage.removeItem('authToken');
      
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Logout failed');
    }
  }
);

// Handle OAuth success callback
export const handleOAuthSuccess = createAsyncThunk(
  'auth/handleOAuthSuccess',
  async (token: string, { rejectWithValue }) => {
    try {
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      
      // Fetch user data
      const response = await apiService.get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.status !== 'success') {
        throw new Error('Failed to get user data');
      }
      
      return {
        user: response.data.data.user,
        token
      };
    } catch (error) {
      console.error('OAuth success handling error:', error);
      localStorage.removeItem('authToken'); // Clean up on error
      return rejectWithValue(error instanceof Error ? error.message : 'Authentication failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // Check if we have a token
      const token = localStorage.getItem('authToken');
      if (!token) {
        // No token available, not authenticated
        return { authenticated: false };
      }
      
      // Get the current user data
      const response = await apiService.get('/auth/me');
      
      if (response.data.status !== 'success') {
        throw new Error('Failed to get user data');
      }
      
      return {
        authenticated: true,
        user: response.data.data.user,
        token
      };
    } catch (error) {
      console.error('Current user fetch error:', error);
      
      // If there was an error, clean up the token as it might be expired
      localStorage.removeItem('authToken');
      
      return rejectWithValue(error instanceof Error ? error.message : 'Authentication failed');
    }
  }
);

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUserPreferences: (state, action: PayloadAction<{ ui_theme?: string; notifications?: boolean }>) => {
      if (state.user) {
        state.user.preferences = {
          ...state.user.preferences,
          ...action.payload
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Login with Provider
      .addCase(loginWithProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithProvider.fulfilled, (state, action) => {
        state.loading = false;
        
        // If we're redirecting to OAuth provider, don't update auth state
        if (action.payload?.redirecting) {
          return;
        }
        
        // Otherwise update auth state with user and token
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(loginWithProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle OAuth Success
      .addCase(handleOAuthSuccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleOAuthSuccess.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(handleOAuthSuccess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        
        // Check if authentication was successful
        if (action.payload.authenticated) {
          state.user = action.payload.user;
          state.token = action.payload.token || null;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });
  }
});

export const { updateUserPreferences } = authSlice.actions;

export default authSlice.reducer; 